import { ButtonProtected } from '@components'

import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useAcceptSuggestion } from '@features/reconciliation/hooks/useAcceptSuggestion'
import { BookkeepingItem as BookkeepingItemType } from '@features/reconciliation/types/bookkeepingItem'
import { BankLine, ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../../../contexts'
import { useSandboxAwareTrack } from '../../../../hooks/useSandboxAwareTrack'

interface SuggestionAcceptButtonProps {
  bankLine: BankLine | null
  suggestedBookkeepingItem?: BookkeepingItemType
  matchSuggestion?: ReconciliationSuggestion
  isLoading?: boolean
}

export const SuggestionAcceptButton = ({
  bankLine,
  suggestedBookkeepingItem,
  matchSuggestion,
}: SuggestionAcceptButtonProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSandboxAwareTrack()
  const sandbox = useSandboxModeContext()
  const {
    hasBankReconciliationWriteScope,
    clearSelection,
    hasDifference,
    differenceType,
    isCrossCurrency,
    setMatchError,
  } = useBankTransactionDrawer()

  const { acceptSuggestion, isAccepting } = useAcceptSuggestion({
    onSuccess: clearSelection,
    onError: (errorMessage) => setMatchError(errorMessage ?? t('something_went_wrong')),
    uiSource: UiSource.AccountingTransactionView,
    uiFlow: BankMatchUiFlow.Matched,
  })

  const handleAcceptClick = useCallback(() => {
    if (sandbox && bankLine && suggestedBookkeepingItem) {
      sandbox.matchBankLineInSandbox(bankLine.id, [suggestedBookkeepingItem])
      clearSelection()
      return
    }

    acceptSuggestion({
      bankLine,
      suggestedBookkeepingItem,
      matchSuggestion,
      differenceType: isCrossCurrency ? null : differenceType,
    })
    track('XXX - Reconciliation - Reconciliation Suggestion Accepted (Client)')
  }, [
    sandbox,
    bankLine,
    suggestedBookkeepingItem,
    clearSelection,
    acceptSuggestion,
    matchSuggestion,
    isCrossCurrency,
    differenceType,
    track,
  ])

  const isAcceptingDisabled = isAccepting || (!isCrossCurrency && hasDifference && !differenceType)

  return (
    <ButtonProtected
      variant="filled"
      size="sm"
      fullWidth
      onClick={handleAcceptClick}
      disabled={hasBankReconciliationWriteScope && isAcceptingDisabled}
      scopes={Scope.BankReconciliationWrite}
      trackingContext={TrackingContext.BankTransactions}
    >
      {t('bank_transactions.drawer.suggestion_accept')}
    </ButtonProtected>
  )
}
